import { Button } from "@/components/generic/Button";
import { Input } from "@/components/generic/Field";
import { Modal } from "@/components/generic/Modal";
import { TextField } from "@/components/generic/TextField";
import { Tooltip } from "@/components/generic/Tooltip";
import { DarkUI } from "@/components/icons/DarkUI";
import { LightUI } from "@/components/icons/LightUI";
import { PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { useOpenInsights } from "@/hooks/use-open-insights";
import { ThemeContext } from "@/providers/theme-context";
import { hasInsightsAccess, isAdmin, useLogout } from "@/services/auth.service";
import { Variant } from "@/types/theme";
import {
  api,
  ApiException,
  isGeneralException,
  isLassoException,
  LassoException,
} from "@/utils/api";
import lassoUrls from "@/utils/lasso-urls";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { HTTPError } from "ky";
import { ExternalLink, LockIcon } from "lucide-react";
import { useContext, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/settings/profile/_profile/")({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Profil",
    },
  },
});

type Inputs = {
  name: string;
  email: string;
  phone: string | null;
};

type InputsPassword = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const schema = z.object({
  name: z.string().min(1, { message: "Navn må ikke være tomt" }),
  email: z.string().email("Ugyldig email"),
  phone: z
    .union([z.string(), z.null()])
    .refine(
      (val) =>
        val === null || val === "" || /^(?:\+45)?[2-9][0-9]{7}$/.test(val),
      { message: "Ugyldigt telefonnummer" },
    ),
});

function RouteComponent() {
  const { setTheme, theme, currentTheme, setVariant, variant } =
    useContext(ThemeContext);
  const context = Route.useRouteContext();
  const { mutate: logout } = useLogout();
  const openInsights = useOpenInsights();

  const [openChangePassword, setOpenChangePassword] = useState(false);

  const logoutAndRedirect = () => {
    logout();
    location.href =
      lassoUrls.getAppsUrl() +
      "/logout?returnUrl=" +
      encodeURIComponent(document.location.href);
  };

  const {
    handleSubmit,
    clearErrors,
    reset,
    setError,
    control,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    context: "profile",
    mode: "onSubmit",
    defaultValues: {
      name: context.user?.name,
      email: context.user?.email,
      phone: context.user?.phoneNumber,
    },
  });

  const {
    handleSubmit: handleSubmitChangePassword,
    control: controlChangePassword,
    clearErrors: clearErrorsChangePassword,
    reset: resetChangePassword,
    formState: { isDirty: isDirtyChangePassword },
    watch: watchChangePassword,
  } = useForm<InputsPassword>({
    context: "changePassword",
    mode: "onChange",
  });

  const watchAllFields = watchChangePassword();

  const areAllFieldsFilled =
    watchAllFields.oldPassword?.length > 0 &&
    watchAllFields.newPassword?.length > 0 &&
    watchAllFields.confirmPassword?.length > 0;

  const onSubmit = async (data: Inputs) => {
    try {
      await api.put("users/me", {
        json: {
          name: data.name,
          email: data.email,
          phoneNumber: data.phone || null,
        },
      });
      if (context.user) {
        context.user.email = data.email;
        context.user.name = data.name;
        if (data.phone) context.user.phoneNumber = data.phone;
      }
      reset(data);
      toast.success("Profil opdateret");
    } catch (err: unknown) {
      if (!(err instanceof HTTPError)) throw err;
      const errorData = await err.response.json<
        Inputs | ApiException | LassoException
      >();
      if (!isGeneralException(errorData) && !isLassoException(errorData)) {
        Object.keys(errorData).forEach((fieldName) => {
          setError(fieldName as keyof Inputs, {
            type: "server",
            message: errorData[fieldName as keyof Inputs] as string | undefined,
          });
        });
      } else if (isLassoException(errorData)) {
        setError("root", {
          type: "server",
          message: errorData.message,
        });
      } else {
        setError("root", {
          type: "server",
          message: "An unexpected error occurred.",
        });
      }
      clearErrors();
    }
  };

  const onSubmitPassword: SubmitHandler<InputsPassword> = async (data) => {
    if (data.newPassword.length < 6) {
      toast.error("Det valgte password skal være mindst 6 karakterer langt");
      return;
    }

    if (data.newPassword !== data.confirmPassword) {
      toast.error("Det valgte og bekræftede password er ikke ens");
      return;
    }

    try {
      await api.post("users/me/changepassword", { json: data });
      toast.success("Adgangskode ændret");
      resetChangePassword();
      setOpenChangePassword(false);
    } catch (err: unknown) {
      if (!(err instanceof HTTPError)) throw err;
      const errorData = (await err.response.json()) as
        | Record<keyof InputsPassword, string[]>
        | ApiException
        | LassoException;

      if (!isGeneralException(errorData) && !isLassoException(errorData)) {
        Object.keys(errorData).forEach((fieldName) => {
          const fieldErrors = errorData[fieldName as keyof InputsPassword];
          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            const errorMessage = fieldErrors[0];
            toast.error(errorMessage);
          }
        });
      } else if (isLassoException(errorData)) {
        toast.error(errorData.errorMessage);
      } else {
        toast.error("An unexpected error occurred.");
      }
    }
  };

  const lightVariants = [Variant.Bordeaux, Variant.DarkBlue, Variant.DarkGreen];
  const darkVariants = [
    Variant.Green,
    Variant.Pink,
    Variant.Blue,
    Variant.Orange,
  ];

  return (
    <>
      <PageHeader
        title="Profil"
        actions={
          <>
            <Button
              intent="secondary"
              onClick={() => setOpenChangePassword(true)}
            >
              Skift adgangskode
            </Button>

            {!isAdmin(context.user) && (
              <>
                <Button
                  animate={true}
                  intent="secondary"
                  onPress={logoutAndRedirect}
                >
                  Log ud
                </Button>

                {hasInsightsAccess(context.user) && (
                  <Button
                    animate={true}
                    intent="primary"
                    onPress={openInsights}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      Gå til Lasso Insights
                      <ExternalLink size={16} />
                    </div>
                  </Button>
                )}
              </>
            )}
          </>
        }
      />
      <PageContent className="flex h-full flex-col">
        <div className="mx-auto flex w-full flex-col px-14 pb-14">
          <Modal
            isDismissable
            size="lg"
            padding="lg"
            isOpen={openChangePassword}
            onOpenChange={(isOpen) => {
              setOpenChangePassword(isOpen);
              if (!isOpen) {
                resetChangePassword();
                clearErrorsChangePassword();
              }
            }}
            icon={<LockIcon className="text-theme-active/80" />}
          >
            <div className="flex max-w-xs flex-col items-center gap-4">
              <span className="text-md text-theme-on-glass font-medium">
                Skift adgangskode
              </span>

              <span className="text-theme-on-glass-variant text-center text-sm">
                Sørg for, at dine loginoplysninger forbliver sikre ved at ændre
                din adgangskode.
              </span>
            </div>

            <div className="w-full pt-4">
              <form
                onSubmit={handleSubmitChangePassword(onSubmitPassword)}
                className="flex w-full flex-col items-center gap-4"
              >
                <TextField
                  className="w-full"
                  control={controlChangePassword}
                  name="oldPassword"
                  type="password"
                  aria-label="Nuværende adgangskode"
                  autoComplete="current-password"
                  placeholder="Nuværende adgangskode"
                  isRequired
                />
                <TextField
                  className="w-full"
                  control={controlChangePassword}
                  name="newPassword"
                  type="password"
                  aria-label="Ny adgangskode"
                  autoComplete="new-password"
                  placeholder="Ny adgangskode"
                  isRequired
                />
                <TextField
                  className="w-full"
                  control={controlChangePassword}
                  name="confirmPassword"
                  type="password"
                  aria-label="Bekræft ny adgangskode"
                  autoComplete="new-password"
                  placeholder="Bekræft ny adgangskode"
                  isRequired
                />
                <div className="flex justify-center gap-6 pt-3.5">
                  <Button animate slot="close" intent="secondary">
                    Fortryd
                  </Button>
                  <Button
                    isDisabled={!isDirtyChangePassword || !areAllFieldsFilled}
                    animate
                    intent="primary"
                    type="submit"
                  >
                    Gem
                  </Button>
                </div>
              </form>
            </div>
          </Modal>
          <div className="flex w-full justify-center">
            <form
              id="profile-form"
              className=""
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="mt-10 flex min-w-sm flex-col gap-6 text-center">
                <span className="font-medium">Personlige oplysninger</span>
                <TextField
                  name="name"
                  type="text"
                  aria-label="Navn"
                  isRequired
                  validate={(value) =>
                    value.trim().length > 0 || "Navn må ikke være tomt"
                  }
                  validationBehavior="native"
                  control={control}
                  placeholder="Navn"
                />

                <Tooltip
                  tooltipTriggerProps={{ delay: 0 }}
                  offset={-240}
                  placement="right"
                  className="flex flex-col"
                  tooltipText="Dette felt kan ikke ændres"
                >
                  <TextField
                    theme="none"
                    className="border-theme-outline-variant/100 text-theme-outline rounded-sm border"
                    isReadOnly
                    name="email"
                    type="email"
                    control={control}
                    placeholder={context.user?.email}
                    aria-label="Email"
                  />
                </Tooltip>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="tel"
                      onChange={(e) => {
                        const value = e.target.value;
                        const filteredValue = value.replace(/[^0-9+]/g, "");
                        const finalValue =
                          filteredValue === "" ? null : filteredValue;
                        field.onChange(finalValue);
                      }}
                      aria-label="Telefonnummer"
                      placeholder="Telefonnummer"
                      value={field.value || ""}
                    />
                  )}
                />
                <Button
                  className="mx-auto"
                  isDisabled={!isDirty || !isValid}
                  intent="primary"
                  type="submit"
                  form="profile-form"
                  size="lg"
                >
                  Gem ændringer
                </Button>
              </div>
            </form>
          </div>

          <div className="mt-10 flex flex-col items-center gap-7 border-0">
            <div className="mx-3 flex justify-between gap-y-7">
              <div className="flex w-full flex-col items-center gap-y-6">
                <span>Tema</span>
                <div className="flex w-full justify-end gap-x-3">
                  <div
                    onClick={() => {
                      setTheme("light");
                      setVariant(Variant.Blue);
                    }}
                    className={`relative flex h-auto w-auto min-w-[initial] cursor-pointer flex-col gap-3 rounded-md p-2 ${
                      theme === "light"
                        ? "border-theme-primary border"
                        : "border border-transparent"
                    }`}
                  >
                    <LightUI />
                  </div>
                  <div
                    onClick={() => {
                      setTheme("dark");
                      setVariant(Variant.Green);
                    }}
                    className={`relative flex h-auto w-auto min-w-[initial] cursor-pointer flex-col gap-3 rounded-md p-2 ${
                      theme === "dark"
                        ? "border-theme-primary hover:border-theme-primary border transition-all duration-350"
                        : "border border-transparent transition-all duration-350"
                    }`}
                  >
                    <DarkUI />
                  </div>
                </div>
              </div>
            </div>
            <div className="mx-3 flex items-center justify-between gap-y-7">
              <div className="flex gap-x-3">
                {(currentTheme === "light" ? lightVariants : darkVariants).map(
                  (themeVariant) => (
                    <div
                      key={themeVariant}
                      className={twMerge(
                        "rounded-lg p-[2px]",
                        variant == themeVariant
                          ? "border-theme-primary border transition-all duration-350"
                          : "border border-transparent transition-all duration-350",
                      )}
                    >
                      <Button
                        intent="secondary"
                        onClick={() => setVariant(themeVariant)}
                        className={twMerge(
                          `!size-8 min-w-0 cursor-pointer p-1`,
                        )}
                        style={{
                          backgroundImage: `linear-gradient(var(--theme-${themeVariant}-gradient))`,
                        }}
                      />
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </PageContent>
    </>
  );
}
